// React
import { memo, cloneElement, isValidElement, ReactElement, Fragment } from "react";

// Interfaces
import { ISimpleIconButtonProps, ITierAddHeaderProps, ITierHeader } from "./interfaces";

// MUI
import { Grid, Stack, Typography } from "@mui/material";

// Components
import GradientHeader from "@Layouts/components/Basics/GradientHeader";
import SimpleIconButton from "@Layouts/components/Buttons/SimpleIconButton";

// Icons
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";

function ActionButtonContent(props: Readonly<ISimpleIconButtonProps>) {
	return <SimpleIconButton {...props} />;
}

const ActionButton = memo(ActionButtonContent);

function RenderContent(props: Readonly<ITierHeader>) {
	const {
		title = "",
		classes = "!font-montserrat !font-[500] !text-[16px] text-default",
		component = null,
		componentProps = null,
	} = props;

	if (component && Boolean(isValidElement(component))) {
		return <Fragment>{cloneElement(component as ReactElement, { ...props, ...componentProps })}</Fragment>;
	}

	return (
		<Typography variant="h5" component="div" className={classes}>
			{title}
		</Typography>
	);
}

function HeaderContent(props: Readonly<ITierHeader>) {
	const { index = 0, dividerProperties = undefined, disabledAddButton = false, loading = false, onActionAdd = () => {} } = props;
	const { show = false, sx = null } = dividerProperties || {};

	const isAddShowed = index === 0;

	return (
		<GradientHeader showDivider={show} sxDivider={sx}>
			<Stack direction="row" justifyContent={"center"} alignContent={"center"} alignItems={"center"} spacing={1}>
				<Stack>
					<RenderContent {...props} index={index} />
				</Stack>
				{isAddShowed && (
					<Stack>
						<ActionButton
							className={"btn-simple-add !text-primary"}
							ariaLabel="Add item"
							disabled={disabledAddButton}
							loading={loading}
							loadingSize={20}
							sxLoading={{ padding: "2px" }}
							icon={<AddCircleOutlineRoundedIcon fontSize="medium" />}
							iconHover={<AddCircleRounded fontSize="medium" />}
							sx={{ padding: "3px" }}
							onClick={onActionAdd}
						/>
					</Stack>
				)}
			</Stack>
		</GradientHeader>
	);
}

const Header = memo(HeaderContent);

export default function TierAddHeader(props: Readonly<ITierAddHeaderProps>) {
	const {
		headers = [],
		containerProps = {
			direction: "row",
			justifyContent: "flex-start",
			columnSpacing: 3,
		},
		disabledAddButton = false,
		readOnly = false,
		actions = {},
		dependencies = undefined,
		onActionAdd = () => {},
	} = props;

	return (
		<Grid container {...containerProps}>
			{headers.map((header, key) => {
				const { title = "", properties = null, style = undefined } = header;
				return (
					<Grid key={`${title}_${key}`} item className="simple-transition" {...properties} style={style}>
						<Header
							{...header}
							index={key}
							disabledAddButton={disabledAddButton}
							actions={actions}
							dependencies={dependencies}
							readOnly={readOnly}
							onActionAdd={onActionAdd}
						/>
					</Grid>
				);
			})}
		</Grid>
	);
}
